import { useFirestoreDatabases, useSetActiveConnection } from '@/query/gcp';
import { FirestoreDatabase } from '@firestore-gui/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../ui/toast';
import {
  extractDatabaseIds,
  getDatabaseTypeForId,
  getDefaultSelectedId,
  isApiNotEnabledError,
  isPermissionDeniedError,
  isPermissionDeniedFromError,
} from './gcp-database-picker.domain';

interface UseGcpDatabasePickerArgs {
  projectId: string;
  onDatabaseSelect: (databaseId: string) => void;
  onBackToProjectSelection?: () => void;
}

export function useGcpDatabasePicker({
  projectId,
  onDatabaseSelect,
  onBackToProjectSelection,
}: UseGcpDatabasePickerArgs) {
  const [userSelectedDatabaseId, setUserSelectedDatabaseId] = useState<string>('');
  const { toast } = useToast();
  const { data, isLoading, error, isError, refetch } = useFirestoreDatabases(projectId);
  const setActiveConnectionMutation = useSetActiveConnection();

  const databases: FirestoreDatabase[] = useMemo(() => data?.databases ?? [], [data?.databases]);
  const databaseIds = extractDatabaseIds(databases);
  const errorMessage = error instanceof Error ? error.message : error ? String(error) : null;
  const isPermissionDenied = isPermissionDeniedError(errorMessage);

  const defaultSelectedId = data?.databases?.length
    ? getDefaultSelectedId(extractDatabaseIds(data.databases))
    : '';

  const selectedDatabaseId = userSelectedDatabaseId || defaultSelectedId || '';

  useEffect(() => {
    if (!isError || !errorMessage) return;
    const code =
      error && typeof error === 'object' && 'code' in error
        ? String((error as { code: string }).code)
        : undefined;

    if (isPermissionDeniedFromError(errorMessage, code)) {
      toast({
        title: 'Permission Denied',
        description: 'Please ensure Firestore Admin API is enabled and you have access.',
        variant: 'destructive',
      });
    } else if (isApiNotEnabledError(errorMessage, code)) {
      toast({
        title: 'API Not Enabled',
        description: 'Firestore Admin API is not enabled. Please enable it in GCP Console.',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [isError, errorMessage, error, toast]);

  const handleSelect = useCallback(async () => {
    if (!selectedDatabaseId) return;

    try {
      await setActiveConnectionMutation.mutateAsync({
        projectId,
        databaseId: selectedDatabaseId,
      });
      toast({
        title: 'Success',
        description: `Connected to ${projectId} / ${selectedDatabaseId}`,
        variant: 'default',
      });
      onDatabaseSelect(selectedDatabaseId);
    } catch (err: unknown) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save connection',
        variant: 'destructive',
      });
    }
  }, [projectId, selectedDatabaseId, onDatabaseSelect, toast, setActiveConnectionMutation]);

  const handleItemClick = useCallback(
    (databaseId: string) => {
      if (selectedDatabaseId === databaseId) {
        handleSelect();
        return;
      }
      setUserSelectedDatabaseId(databaseId);
    },
    [selectedDatabaseId, handleSelect]
  );

  const getDatabaseType = useCallback(
    (databaseId: string) => getDatabaseTypeForId(databases, databaseId),
    [databases]
  );

  return {
    loading: isLoading,
    error: errorMessage,
    isPermissionDenied,
    databaseIds,
    selectedDatabaseId,
    databases,
    fetchDatabases: refetch,
    handleSelect,
    handleItemClick,
    getDatabaseType,
    onBackToProjectSelection,
    projectId,
  };
}
